import { Toggle } from '@purpur/library';
import styled, { css } from 'styled-components';

import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';
import { NAVIGATION_OFFSET_TRANSITION_CSS_VARIABLE } from '@telialt/utils/src/NavigationUtils/constants';

type SpacerHeightProps = {
    $desktopHeight: number;
    $mobileHeight: number;
};
export const ComparisonTableStickySpacer = styled.div<SpacerHeightProps>`
    ${({ $mobileHeight, $desktopHeight }) => css`
        height: ${`${$mobileHeight}px`};

        ${purpurMedia('lg')`
            height: ${`${$desktopHeight}px`};
        `};
    `};
`;

export const StyledComparisonTable = styled.table`
    width: calc(100% + ${getPurpurSpacing('150')} + ${getPurpurSpacing('150')});
    margin: 0 -${getPurpurSpacing('150')};
    border-collapse: separate;
    border-spacing: 0;
    border-left-style: hidden;
    border-right-style: hidden;
    table-layout: fixed;
    // Workaround for height: 100% use inside table cells
    height: 1px;

    padding: 0 ${getPurpurSpacing('200')};

    ${purpurMedia('md')`
        padding: 0;
    `};
`;

type StickyProps = {
    $isSticky?: boolean;
    $topOffset: number;
};

export const ComparisonTableHead = styled.thead<StickyProps>`
    border-top: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
    border-bottom: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
    background: ${getPurpurColor('functionalWhite')};

    ${({ $isSticky, $topOffset }) =>
        $isSticky &&
        css`
            position: sticky;
            top: ${$topOffset}px;
            z-index: 1;
            transition: var(${NAVIGATION_OFFSET_TRANSITION_CSS_VARIABLE});
        `};

    tr:first-child > th,
    tr:first-child > td {
        border-top: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
        border-bottom: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
        border-right: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
        &:last-child {
            border-right: none;
        }
    }
    ${purpurMedia('lg')`
        tr:first-child > th,
        tr:first-child > td {
            padding-top: ${getPurpurSpacing('200')};
            border: none;
        }
    `};
`;

export const ComparisonTableHeadTitleCell = styled.th`
    display: none;
    ${purpurMedia('xl')`
        padding: 0 ${getPurpurSpacing('150')};
        position: relative;
        display: table-cell;
        min-width: 19.875rem;
    `};
`;

export const ComparisonTableHeadProductCell = styled.td`
    height: 100%;

    padding: 0 ${getPurpurSpacing('200')};
    &:first-of-type {
        padding-left: 0;
    }
    &:last-of-type {
        padding-right: 0;
    }
    ${purpurMedia('md')`
        padding: 0;
    `};
    ${purpurMedia('lg')`
        padding: 0 ${getPurpurSpacing('150')};
        min-width: 19.875rem;
    `};
`;

type VisibleProps = {
    $isVisible?: boolean;
};

export const ComparisonTableProductContainer = styled.div<VisibleProps>`
    height: 100%;
    display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
`;

export const ComparisonTableHeadTitle = styled.div`
    font-size: ${getPurpurTypography('scale200')};
    font-weight: ${getPurpurTypography('weightBold')};
    color: ${getPurpurColor('textDefault')};
    text-align: center;
`;

export const ComparisonTableHeadCategoryName = styled.div`
    color: ${getPurpurColor('textDefault')};
    font-size: ${getPurpurTypography('scale100')};
    font-weight: ${getPurpurTypography('weightNormal')};
    margin-top: ${getPurpurSpacing('100')};
`;

export const DifferencesOnlyToggle = styled(Toggle)`
    background: ${getPurpurColor('gray50')};
    border-radius: ${getPurpurBorder('radiusMd')};
    border: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray300')};
    padding: ${getPurpurSpacing('100')} ${getPurpurSpacing('200')};
    position: absolute;
    bottom: 0;
    left: ${getPurpurSpacing('150')};
    right: ${getPurpurSpacing('150')};
    width: auto;
`;

export const MobileDifferencesOnlyRow = styled.tr`
    > td {
        background: ${getPurpurColor('functionalWhite')};
        padding: ${getPurpurSpacing('100')} 0;
    }
    ${purpurMedia('md')`
        > td {
            padding: ${getPurpurSpacing('100')} ${getPurpurSpacing('200')};
        }
    `};
    ${purpurMedia('lg')`
        > td {
            padding: ${getPurpurSpacing('100')} 0;
        }
    `};
    ${purpurMedia('xl')`
        display: none;
    `};
`;

type RowProps = {
    $isEven?: boolean;
};

export const FeatureRow = styled.tr<RowProps>`
    background: ${({ $isEven }) => ($isEven ? getPurpurColor('gray50') : getPurpurColor('brandWhite'))};
`;

export const MobileFeatureNameRow = styled.tr<RowProps>`
    background: ${({ $isEven }) => ($isEven ? getPurpurColor('gray50') : getPurpurColor('brandWhite'))};
    ${purpurMedia('xl')`
        display: none;
    `};

    th {
        text-align: left;
        font-weight: ${getPurpurTypography('weightNormal')};
        padding: ${getPurpurSpacing('200')} ${getPurpurSpacing('200')} 0;
    }
`;

export const ClassificationSection = styled.tbody`
    &:after {
        content: '';
        display: block;
        padding-bottom: ${getPurpurSpacing('400')};
    }
    &:first-of-type:before {
        content: '';
        display: block;
        padding-top: ${getPurpurSpacing('400')};
    }
    ${purpurMedia('lg')`
        &:after {
            padding-bottom: ${getPurpurSpacing('800')};
        }
        &:first-of-type:before {
            padding-top: ${getPurpurSpacing('800')};
        }
    `};
`;

export const ClassificationNameCell = styled.th`
    color: ${getPurpurColor('brandOffBlack')};
    font-weight: ${getPurpurTypography('weightBold')};
    padding-bottom: ${getPurpurSpacing('300')};
    text-align: left;
    font-size: ${getPurpurTypography('scale200')};

    ${purpurMedia('md')`
        font-size: ${getPurpurTypography('scale400')};
    `};
`;

export const FeatureNameCell = styled.th`
    display: none;

    ${purpurMedia('xl')`
        display: table-cell;
        font-weight: ${getPurpurTypography('weightNormal')};
        text-align: left;
        padding: ${getPurpurSpacing('150')} ${getPurpurSpacing('200')} ${getPurpurSpacing('150')} ${getPurpurSpacing('300')};
    `};
`;

export const FeatureValueCell = styled.td`
    font-weight: ${getPurpurTypography('weightBold')};
    color: ${getPurpurColor('brandOffBlack')};
    padding: ${getPurpurSpacing('150')} ${getPurpurSpacing('200')};
    text-align: center;
    word-break: break-word;

    &:last-child {
        padding-right: ${getPurpurSpacing('300')};
    }
`;
